import { defineStore } from "pinia";
import { computed, nextTick, ref } from "vue";
import {
  monsterLevel1,
  monsterLevel2,
  monsterLevel3,
  monsterLevel4,
} from "awakening-of-war-shared";
import type { TowerMonsterType } from "awakening-of-war-shared";
import { socketEmitAsync } from "@/utils/web-socket";
import { equipCollection } from "@/core/equip";
import type { EquipItemType, EquipQuality, EquipType } from "@/types/equip";
import { randomOne } from "@/utils";
import { equipIcon } from "@/views/item/data";
import { EquipPriceEnum } from "@/enums";
import { roguelikeScoreSaveApi } from "@/api/roguelike-score";
import { equipAffixCollectionRogue } from "@/core/equip/affixRogue";
import { pick } from "lodash";

/** 静态变量基础配置 */
const VERSION = "1.1.0";
const STORAGE_KEY = `war-soul-${import.meta.env.VITE_ENV_MODE}-rogue_saved`;
// 初始生存时间
const REMAINING_TIME = 5 * 60 * 1000;
// 怪物掉落基础金币
const DROP_GOLD_BASIC = 100;
// BOSS金币加成系数 x倍
const DROP_GOLD_BOSS_R = 1.5;
// 难度加成金币系数
const DROP_GOLD_LEVEL_R = 1;
// BOSS经验加成系数
const DROP_EXP_BOSS_R = 3;
/**
 * @description 每级血量上限
 */
const P_L_HP = 30;
/**
 * @description 每只西瓜持有奖励
 */
const WM_HP = 100;
/**
 * @description 每只西瓜回复的血量
 */
const WM_R_HP = 1000;
/**
 * 怪物刷新间隔
 */
const M_C_CD = 4 * 1000;
/**
 * 技能冷却表
 */
const CD_TIME_TABLE: Record<string, number> = {
  thump: 10 * 1000,
  harvest: 45 * 1000,
  assault: 45 * 1000,
  heavyInjury: 10 * 1000,
  burst: 10 * 1000,
  nothingness: 60 * 1000,
  precise: 5 * 1000,
  impact: 45 * 1000,
  windUp: 45 * 1000,
  drawChop: 60 * 1000,
  gentle: 90 * 1000,
};
export const useRoguelikeGameStore = defineStore("minigames-roguelike", () => {
  // 难度配置
  const DEGREE_CONFIG = [
    {
      name: "学徒之路(简单)",
      desc: "初始难度，适合新手玩家熟悉游戏机制",
      monsterGrowX: 0.9, // 怪物血量
      bossGrowX: 1.1, // 怪物血量
      scoreX: 0.5,
      levelEasedownX: 0.2, // 生命流逝随着等级变化的系数 等于怪物攻击
      levelGapX: 60, // 难度提升间隔秒数
      startGold: 2000,
      expXFn() {
        return Math.pow(monster.value!.level, 0.7) * 2;
      },
      goldXFn() {
        return 1 + Math.pow(monster.value!.level, 0.4);
      },
      btnType: "success",
    },
    {
      name: "勇者征程(困难)",
      desc: "已经掌握基本操作，开始面对更严峻的挑战",
      monsterGrowX: 1,
      bossGrowX: 1.2,
      levelGapX: 50,
      scoreX: 1,
      levelEasedownX: 0.3,
      startGold: 1000,
      expXFn() {
        return Math.pow(monster.value!.level, 0.5) * 1.9;
      },
      goldXFn() {
        return 1 + Math.pow(monster.value!.level, 0.3);
      },
      btnType: "warning",
    },
    {
      name: "英灵试炼(地狱)",
      desc: "面对更强的怪物，并且只会遇到已解锁的装备",
      monsterGrowX: 1.1,
      bossGrowX: 1.3,
      levelGapX: 45,
      scoreX: 2,
      levelEasedownX: 0.4,
      startGold: 1000,
      expXFn() {
        return Math.pow(monster.value!.level, 0.3) * 1.8;
      },
      goldXFn() {
        return 1 + Math.pow(monster.value!.level, 0.2);
      },
      btnType: "danger",
    },
  ];

  // 响应式状态
  class GameState {
    // 游戏是否开始
    started = false;
    // 游戏进行时间
    gameTime = 0;
    // 剩余时间
    maxRemainingTime = REMAINING_TIME;
    remainingTime = REMAINING_TIME;
    // 击杀boss数
    killBoss = 0;
    // 击杀怪物数
    killMonster = 0;
    // 是否展示结果
    showResult = false;
    // 金币
    gold = 1000;
    // 经验
    exp = 0;
    // 准备登场的BOSS
    readyBoss?: Monster;
    // 登场倒计时
    readyTime = 0;
    // 信息是否滚动
    isMessageScrollAuto = true;
    // 分数上传状态信息
    uploadScoreText = "";
    // 额外攻击力奖励
    extraAtk = 0;
    // 倍速
    ddr = 1;
    // 怪物刷新时间
    monsterCreateCD = M_C_CD;
  }
  function battleSpeed() {
    if (gameState.value.ddr++ == 3) {
      gameState.value.ddr = 1;
    }
  }
  const degree = ref(0);
  const degreeConfig = computed(() => {
    return DEGREE_CONFIG[degree.value];
  });
  /** 响应式状态 */
  const gameState = ref(new GameState());
  // 玩家等级
  const playerLevel = computed(() => {
    return Math.floor(Math.sqrt(gameState.value.exp));
  });

  // 每个多少时间提升难度等级
  const levelGap = computed(() => degreeConfig.value.levelGapX * 1000);
  // 难度等级
  const level = computed(() => {
    return 1 + Math.floor(gameState.value.gameTime / levelGap.value);
  });
  // 战斗消息
  const fightMessageList = ref<string[]>([]);
  // 用户可用的装备ids
  const userEquipCollectionIds = ref<number[]>([]);
  // 怪物属性
  type Monster = TowerMonsterType & { isBoss?: boolean; level: number };
  const monster = ref<Monster & { hasFix?: boolean }>();
  // 怪物总hp
  const monsterHp = ref(0);
  // 怪物当前hp
  const monsterCurHp = ref(0);
  // 玩家基础属性
  const isCheat = false;
  const playerAttr = !isCheat
    ? {
        atk: 200,
        atksp: 100,
        crt: 0, // 装备提供的暴击率补正
        bcrt: 10, // 基础暴击率
        crtd: 150,
        heat: 0,
        hr: 0,
      }
    : {
        atk: 2000,
        atksp: 200,
        crt: 0, // 装备提供的暴击率补正
        bcrt: 10, // 基础暴击率
        crtd: 150,
        heat: 0,
        hr: 0,
      };
  // 玩家收集的装备
  const playerEquips = ref<EquipItemType[]>([]);

  const playerEquipsFilters = computed(() => {
    // 生命值加成
    let watermelonValue = 0;
    // 经验加成
    let moreExpValue = 0;
    // 金币加成
    let moreGoldValue = 0;
    // 是否开启第四个战利品选择
    let selectiveExpansion = 0;
    // 战利品升级
    let upgradeLoot = 0;
    // 甲
    let armor = 0;
    // 帽
    let helmet = 0;
    // 鞋
    let shoes = 0;
    //
    const skillMap = new Map<string, number>();

    playerEquips.value.forEach((item) => {
      const { id, attrs, quality, type } = item;
      if (quality !== "legend") return;
      if (id === "recoverHp") watermelonValue++;
      if (id === "moreExp") moreExpValue++;
      if (id === "moreGold") moreGoldValue++;
      if (id === "selectiveExpansion") selectiveExpansion++;
      if (id === "upgradeLoot") upgradeLoot++;
      if (type === "armor") armor++;
      if (type === "helmet") helmet++;
      if (type === "shoes") shoes++;
      attrs.special?.forEach(({ key }) => {
        const value = skillMap.get(key) ?? 0;
        skillMap.set(key, value + 1);
      });
    });
    const armorLevel = Math.min(Math.floor(armor / 2), 15);
    const helmetLevel = Math.min(Math.floor(helmet / 2), 15);
    const shoesLevel = Math.min(Math.floor(shoes / 2), 15);

    const armorRemaintimeEaseDown = parseFloat(
      (armorLevel * 0.02 + helmetLevel * 0.02 + shoesLevel * 0.02).toFixed(2)
    );

    return {
      watermelonValue,
      moreExpValue,
      moreGoldValue,
      selectiveExpansion,
      upgradeLoot,
      armorRemaintimeEaseDown,
      skillMap,
    };
  });
  function getSkillValueBySkill(key: string) {
    return playerEquipsFilters.value.skillMap.get(key) ?? 0;
  }
  const skillKeys = [
    "thump",
    "harvest",
    "assault",
    "heavyInjury",
    "burst",
    "nothingness",
    "precise",
    "impact",
    "windUp",
    "drawChop",
    "onikiri",
    "gentle",
    "recoverHp",
  ];
  type SkillType = {
    name: string;
    value: number;
    type: string;
  };
  const skillBtns = computed<SkillType[]>(() => {
    return skillKeys.filter(getSkillValueBySkill).map((type) => {
      return {
        name: equipAffixCollectionRogue[type].name,
        value: getSkillValueBySkill(type),
        type,
      };
    });
  });
  const skillCoutDownMap = ref<Map<string, { max: number; cur: number }>>(
    new Map()
  );
  // 装备类型
  const equipTypes: EquipType[] = [
    "weapon",
    "helmet",
    "armor",
    "shoes",
    "jewelry",
  ];
  //    装备收集数量等级
  const equipsMountLevel = computed(() => {
    const res = equipTypes.map((k) => {
      const value = playerEquips.value.filter(({ type }) => k === type).length;
      const level = Math.min(Math.floor(value / 2), 15);
      let desc = "";
      switch (k) {
        case "weapon":
          desc = `攻击力额外提高<span class="grow">${1 * level}%</span>`;
          break;
        case "helmet":
          desc = `破防额外提高<span class="grow">${
            1 * level
          }%</span>，生命力流逝减缓${2 * level}%`;
          break;
        case "armor":
          desc = `命中额外增加<span class="grow">${
            2 * level
          }%</span>，生命力流逝减缓${2 * level}%`;
          break;
        case "shoes":
          desc = `攻速额外提高<span class="grow">${
            1 * level
          }%</span>，生命力流逝减缓${2 * level}%`;
          break;
        case "jewelry":
          desc = `基础暴击额外增加<span class="grow">${1 * level}%</span>`;
          break;
        default:
          break;
      }
      return {
        type: k,
        icon: equipIcon[k],
        value,
        level,
        desc,
      };
    });

    return res.concat([
      {
        type: "" as EquipType,
        icon: "🔋智慧宝石",
        value: playerEquipsFilters.value.moreExpValue,
        level: 0,
        desc: `经验获取提高<span class="grow">${
          playerEquipsFilters.value.moreExpValue * 10
        }%</span>`,
      },
      {
        type: "" as EquipType,
        icon: "🔋财富宝石",
        value: playerEquipsFilters.value.moreGoldValue,
        level: 0,
        desc: `金币获取提高<span class="grow">${
          playerEquipsFilters.value.moreGoldValue * 10
        }%</span>`,
      },
      {
        type: "" as EquipType,
        icon: "🍉西瓜",
        value: playerEquipsFilters.value.watermelonValue,
        level: 0,
        desc: `生命力上限提高<span class="grow">${
          playerEquipsFilters.value.watermelonValue * WM_HP
        }</span>`,
      },
    ]);
  });
  // 玩家计算属性
  const playerComputedAttr = computed(() => {
    const attrWithEquips = playerEquips.value.reduce(
      (acc, cur) => {
        const {
          attrs: {
            basic: { atk = 0, atksp = 0, crt = 0, crtd = 0, heat = 0, hr = 0 },
          },
          quality,
        } = cur;
        return {
          atk: acc.atk + getBasicAttrValue(atk, quality),
          atksp: acc.atksp + getBasicAttrValue(atksp, quality),
          crt: acc.crt + getBasicAttrValue(crt, quality),
          bcrt: acc.bcrt,
          crtd: acc.crtd + getBasicAttrValue(crtd, quality),
          heat: acc.heat + getBasicAttrValue(heat, quality),
          hr: acc.hr + getBasicAttrValue(hr, quality),
        };
      },
      { ...playerAttr }
    );
    equipsMountLevel.value.forEach(({ type, level }) => {
      switch (type) {
        case "weapon":
          attrWithEquips.atk += Number(
            (attrWithEquips.atk * (0.01 * level)).toFixed(0)
          );
          break;
        case "helmet":
          attrWithEquips.heat += Number(
            (attrWithEquips.heat * (0.01 * level)).toFixed(0)
          );
          break;
        case "armor":
          attrWithEquips.hr += 2 * level;
          break;
        case "shoes":
          attrWithEquips.atksp += Number(
            (attrWithEquips.atksp * (0.01 * level)).toFixed(3)
          );
          break;
        case "jewelry":
          attrWithEquips.bcrt += 1 * level;
          break;
        default:
          break;
      }
    });
    const fcrt = (100 + attrWithEquips.crt) * attrWithEquips.bcrt;
    let fhr = calculateHitAndDodgeChances(attrWithEquips.hr, 0).hitChance;
    // 轻灵
    const swiftnessValue = getSkillValueBySkill("swiftness");
    if (swiftnessValue) {
      attrWithEquips.atksp += Math.ceil(
        Math.abs(attrWithEquips.atksp - playerAttr.atksp) * swiftnessValue * 0.2
      );
    }

    //  暴虐
    const longYaValue = getSkillValueBySkill("longYa");
    if (longYaValue) {
      const heatR = Math.floor(attrWithEquips.heat * 0.7);
      attrWithEquips.heat -= heatR;
      attrWithEquips.atk += Math.ceil(heatR * (1 + 0.1 * longYaValue));
    }

    //  黯灭
    const desolatorValue = getSkillValueBySkill("desolator");
    if (desolatorValue) {
      const crtR = Math.floor(attrWithEquips.crt * 0.5);
      attrWithEquips.crt -= crtR;
      attrWithEquips.crtd += Math.ceil(crtR * (1 + 0.1 * desolatorValue));
    }

    //  夜莺
    const nightingaleValue = getSkillValueBySkill("nightingale");
    if (nightingaleValue) {
      attrWithEquips.atk += Math.ceil(fcrt / 100);
    }

    // 破阵
    const breakValue = getSkillValueBySkill("break");
    if (breakValue) {
      attrWithEquips.atk += Math.ceil(attrWithEquips.atk * breakValue * 0.1);
    }

    // 怜悯
    const pityValue = getSkillValueBySkill("pity");
    if (pityValue) {
      fhr = 90;
    }
    //   怪物修改属性
    monster.value?.special?.forEach(
      ({ key, name, data: { multiplier, value, hpPercent } }) => {
        switch (key) {
          case "slow":
            {
              attrWithEquips.atksp -= value;
            }
            break;

          default:
            break;
        }
      }
    );
    attrWithEquips.atk += gameState.value.extraAtk;
    return {
      ...attrWithEquips,
      fcrt,
      fhr,
    };
  });

  // 玩家血量上限
  const playerMaxRemainingTime = computed(() => {
    return (
      gameState.value.maxRemainingTime +
      playerLevel.value * P_L_HP * 600 +
      playerEquipsFilters.value.watermelonValue * WM_HP * 600
    );
  });

  const attackGap = computed(() => {
    const gap = Math.ceil(100000 / playerComputedAttr.value.atksp);
    if (gap < 0) return Infinity;
    return gap;
  });

  //    非响应式状态
  class UnreactiveState {
    // 动画帧
    raf: number = 0;
    // 攻击冷却计时器
    attackTimer = attackGap.value;
    // 动画帧开始时间
    startTime = 0;

    // 升级产生的装备随机次数
    equipRandomTimes = 0;
    // BOSS上次登场难度等级
    readBossLevel = 2;
    // 连击计数器
    attackCount = 0;
    // 连击计时器
    attackCountTimer = 0;
    // 特殊BOSS打过的数量
    specialBoss = 0;
  }
  let us = new UnreactiveState();

  // 攻击间隔（冷却值）

  // 初始化游戏状态
  function initGame() {
    gameState.value = new GameState();
    gameState.value.gold = degreeConfig.value.startGold;

    us = new UnreactiveState();

    fightMessageList.value = [];
    playerEquips.value = [];
    waitSelectEquips.value = [];
    isStop.value = false;
  }
  function debutMonster(m: Monster) {
    us.attackCount = 0;
    monster.value = m;
    monsterHp.value = monster.value.hp;
    monsterCurHp.value = monster.value.hp;
  }
  const hpRate = computed(() =>
    level.value < 10 ? 1 : Math.pow(level.value - 10, 3) / 100 + 1
  );
  // 创建怪物
  function createMonster(isBoss = false) {
    let tempMonster: Monster;
    const maxIndex = Math.min(4, Math.ceil(level.value / 6));
    const monsterList = [
      monsterLevel1,
      monsterLevel2,
      monsterLevel3,
      monsterLevel4,
    ]
      .slice(Math.max(maxIndex - 1, 0), maxIndex)
      .flat();
    if (!isBoss) {
      // 生成小怪
      tempMonster = Object.assign(
        { level: level.value },
        randomOne(monsterList)
      );
      tempMonster.hp = Math.ceil(
        (Math.pow(level.value, degreeConfig.value.monsterGrowX) / 20) *
          hpRate.value *
          tempMonster.hp
      );
    } else {
      // 生成BOSS
      tempMonster = Object.assign(
        { level: level.value },
        randomOne(monsterList)
      );
      tempMonster.hp = Math.ceil(
        (Math.pow(level.value, degreeConfig.value.bossGrowX) / 10) *
          hpRate.value *
          tempMonster.hp
      );

      tempMonster.isBoss = true;
      tempMonster.name += "领主";
    }
    return tempMonster;
  }
  async function meetGameNeeds(dg: number) {
    await getUserEquipCollection(dg);
    if (userEquipCollectionIds.value.length < 20) {
      ElMessageBox.alert("您已点亮的装备不足，请解锁20件装备后再来吧！");
      return false;
    }
    return true;
  }
  // 开始游戏
  async function startGame(dg = 0) {
    if (!(await meetGameNeeds(dg))) return;
    degree.value = dg;
    initGame();
    debutMonster(createMonster());
    gameState.value.started = true;
    resumeGame();
  }
  // 结算游戏
  async function cancelGame() {
    stopGame();
    isStop.value = false;
    gameState.value.showResult = true;
    if (gameState.value.gameTime >= 5 * 60 * 1000) {
      gameState.value.uploadScoreText = "正在上传您的分数，请稍后...";
      await roguelikeScoreSaveApi({
        score: countScoreFn.total(),
        duration: gameState.value.gameTime,
        gold: gameState.value.gold,
        memo: "",
      });
      gameState.value.uploadScoreText = "已上传您的分数";
    } else {
      gameState.value.uploadScoreText = "生存时间过短，不支持上传分数";
    }
    localStorage.removeItem(STORAGE_KEY);
  }
  // 结算后继续游戏
  function continueGame() {
    gameState.value.showResult = false;
    resumeGame();
    writeSaved();
  }
  // 真正结束游戏
  function closeGame() {
    initGame();
  }
  // 暂停游戏
  function stopGame() {
    cancelAnimationFrame(us.raf);
  }
  // tab切换
  // 继续游戏
  function resumeGame() {
    us.startTime = 0;
    stopGame();
    us.raf = requestAnimationFrame(battleFrame);
  }
  const isStop = ref(false);
  document.addEventListener("visibilitychange", () => {
    if (!gameState.value.started) return;
    if (gameState.value.showResult) return;
    if (waitSelectEquips.value.length) return;
    if (document.visibilityState === "hidden") {
      handleStopGame();
    }
  });
  function handleStopGame() {
    stopGame();
    isStop.value = true;
  }
  function handleResumeGame() {
    isStop.value = false;
    if (waitSelectEquips.value.length) return;
    resumeGame();
  }
  // 战斗帧
  function battleFrame(timestamp: number) {
    us.raf = requestAnimationFrame(battleFrame);
    if (us.startTime) {
      if (us.attackTimer === 0) {
        if (monster.value) {
          // 攻击
          attackAction();
          us.attackTimer = attackGap.value;
        }
      }
      // 帧间隔
      const timeLap =
        (timestamp - us.startTime) * gameState.value.ddr * (isCheat ? 2 : 1);
      // 游戏进度
      gameState.value.gameTime += timeLap;
      // 怪物生成
      if (gameState.value.monsterCreateCD === 0) {
        if (!monsterCurHp.value) {
          gameState.value.monsterCreateCD = Math.round(
            2 + Math.random() * M_C_CD
          );
          debutMonster(createMonster());
        }
      } else {
        gameState.value.monsterCreateCD = Math.max(
          gameState.value.monsterCreateCD - timeLap,
          0
        );
      }
      // 剩余时间
      const BOSS_DOUBLE_EASEDOWN = monster.value?.isBoss ? 2 : 1;
      const ARMOR_RT_EASEDOWN =
        1 - playerEquipsFilters.value.armorRemaintimeEaseDown;
      const LEVEL_EASEDOWN = Math.pow(
        level.value,
        degreeConfig.value.levelEasedownX
      );
      gameState.value.remainingTime = Math.max(
        gameState.value.remainingTime -
          Math.ceil(
            timeLap * ARMOR_RT_EASEDOWN * BOSS_DOUBLE_EASEDOWN * LEVEL_EASEDOWN
          ),
        0
      );
      // 攻击冷却
      us.attackTimer = Math.max(us.attackTimer - timeLap, 0);
      // 检测boss是否该出现了
      checkBossReady(timeLap);
      // 刷新技能冷却
      skillCoutDownMap.value.forEach(({ max, cur }, type) => {
        if (!cur) return;
        const newMax = countSkillCD(type, getSkillValueBySkill(type));
        if (newMax < max) {
          skillCoutDownMap.value.set(type, { max: newMax, cur: newMax });
        } else {
          skillCoutDownMap.value.set(type, {
            max,
            cur: Math.max(cur - timeLap, 0),
          });
        }
      });
    }
    us.startTime = timestamp;
    if (gameState.value.remainingTime === 0) {
      cancelGame();
    }
    // 60分钟游戏结束
    if (gameState.value.gameTime > 60 * 60 * 1000) {
      gameState.value.remainingTime = 0;
      cancelGame();
    }
  }
  // 命中行为
  function hitAction(damage: number) {
    if (gameState.value.gameTime - us.attackCountTimer < 2000) {
      us.attackCount++;
    } else {
      us.attackCount = 0;
    }
    us.attackCountTimer = gameState.value.gameTime;
    monsterCurHp.value = Math.max(monsterCurHp.value - damage, 0);
    // 命中钩子
    // 追击
    const chasingValue = getSkillValueBySkill("chasing");
    if (chasingValue && Math.random() * 100 < 10 + 2 * chasingValue) {
      pushMessage(`<b>${equipAffixCollectionRogue["chasing"].name}</b>！`);
      attackAction();
    }
    // 分裂
    const splitValue = getSkillValueBySkill("split");
    if (splitValue && Math.random() * 100 < 5 + 1 * splitValue) {
      pushMessage(`<b>${equipAffixCollectionRogue["chasing"].name}</b>！`);
      attackAction();
      attackAction();
    }
  }
  // 攻击行为
  function attackAction(skill?: SkillType) {
    if (!monster.value) return;
    let isAtk = true;
    let text = "";
    let { def, evs, crtdef } = monster.value;
    let { atk, fcrt, crtd, heat, hr, fhr } = playerComputedAttr.value;
    let isCrt = Math.random() * 1000000 < fcrt * (100 - crtdef);

    // 增加debuff文本
    function addDebuff(content: string) {
      text += `<i>${content}</i>，`;
    }
    function addSkillText(type: string) {
      text += `<b>${equipAffixCollectionRogue[type].name}</b>，`;
    }

    // 被动技能

    // 碎骨
    const crushBoneValue = getSkillValueBySkill("crushBone");
    if (crushBoneValue) {
      def = Math.max(def - Math.ceil(def * 0.2 * crushBoneValue), 0);
      addSkillText("crushBone");
    }
    // 噩兆
    const swanElegyValue = getSkillValueBySkill("swanElegy");
    if (
      swanElegyValue &&
      monsterCurHp.value / monsterHp.value < 0.1 * swanElegyValue
    ) {
      isCrt = true;
      addSkillText("swanElegy");
    }
    // 帷幕
    const parochethValue = getSkillValueBySkill("parocheth");
    if (
      parochethValue &&
      monsterCurHp.value / monsterHp.value < 0.1 * parochethValue
    ) {
      fhr = 100;
      addSkillText("parocheth");
    }

    // 前置怪物钩子
    monster.value.special?.forEach(
      ({ key, name, data: { multiplier, value, hpPercent } }) => {
        if (!monster.value) return 0;
        switch (key) {
          case "resist":
            {
              if ((monsterCurHp.value * 100) / monsterHp.value < hpPercent) {
                addDebuff(name);
                def += value;
              }
            }
            break;
          case "survival":
            {
              if ((monsterCurHp.value * 100) / monsterHp.value < hpPercent) {
                addDebuff(name);
                evs += value;
              }
            }
            break;
          case "weak":
            {
              addDebuff(name);
              if (value) {
                atk = Math.ceil(atk - multiplier);
              } else {
                atk = Math.ceil(atk * (1 - multiplier / 100));
              }
            }
            break;
          case "fearless":
            {
              if ((monsterCurHp.value * 100) / monsterHp.value > hpPercent) {
                addDebuff(name);
                def += value;
              }
            }
            break;
          case "bedrock":
            {
              if (Math.random() < 0.5) {
                addDebuff(name);
                def += value;
              }
            }
            break;
          case "slow":
            {
              addDebuff(name);
            }
            break;
          case "stout":
            {
              if ((monsterCurHp.value * 100) / monsterHp.value < hpPercent) {
                addDebuff(name);
                def += value;
              }
            }
            break;
          case "intimidate":
            {
              addDebuff(name);
              if (value) {
                heat = Math.ceil(heat - multiplier);
              } else {
                heat = Math.ceil(heat * (1 - multiplier / 100));
              }
            }
            break;
          default:
            break;
        }
      }
    );

    const { dodgeChance } = calculateHitAndDodgeChances(hr, evs + 30);
    let hitChance = fhr;
    const randomValue = Math.random() * 100;

    if (skill) {
      hitChance += 10;
      text += `使用 <span class="legend">${skill.name}</span>，`;
      // 攻击触发钩子
      switch (skill.type) {
        case "recoverHp": // 吃西瓜
          removeEquipByType(skill.type);
          recoverHp(WM_R_HP);
          isAtk = false;
          break;
        case "burst": // 爆发
          isCrt = true;
          break;
        case "drawChop": // 拔刀斩
          hitChance = 100;
          break;
        case "precise": // 精准
          hitChance = 100;
          break;
        case "onikiri": // 鬼斩
          removeEquipByType(skill.type);
          hitChance = 100;
          const d = Math.ceil(monsterHp.value / 2);
          monsterCurHp.value = Math.max(monsterCurHp.value - d, 0);
          isAtk = false;
          text += `造成 <span class="hp">${d}</span> 伤害`;
          break;
        default:
          break;
      }
    }
    if (isAtk) {
      // 暴击修正后
      // 暴击伤害
      const crtdRate = (isCrt ? crtd : 100) / 100;
      // 残忍
      const cruelValue = getSkillValueBySkill("cruel");
      if (cruelValue && isCrt) {
        def = Math.max(def - Math.ceil(def * 0.3 * cruelValue), 0);
        addSkillText("cruel");
      }

      // 计算伤害
      function countDamage() {
        if (!monster.value) return 0;
        const heatDefR = (150 + heat) / (150 + def);
        // 算伤害
        let damage = Math.ceil(atk * crtdRate * heatDefR);
        isCrt && (text += "暴击，");
        if (skill) {
          // 伤害计算钩子
          switch (skill.type) {
            case "thump": // 重击
              damage = Math.ceil(2 * damage);
              break;

            case "harvest": // 收割
              damage = Math.ceil(
                (monsterHp.value - monsterCurHp.value) * 0.1 * heatDefR
              );
              break;
            case "assault": // 冲锋
              damage = Math.ceil(monsterCurHp.value * 0.1 * heatDefR);
              break;
            case "windUp": // 收尾
              damage = Math.ceil(
                (monsterHp.value - monsterCurHp.value) *
                  (0.1 + 0.02 * skill.value) *
                  heatDefR
              );
              break;
            case "impact": // 冲击
              damage = Math.ceil(
                monsterCurHp.value * (0.1 + 0.02 * skill.value) * heatDefR
              );
              break;
            case "heavyInjury": // 重创
              isCrt && (damage = Math.ceil((3 + skill.value * 0.5) * damage));
              break;
            case "nothingness": // 虚无
              damage = Math.ceil(monsterHp.value * (0.08 * skill.value));
              break;

            case "drawChop": // 拔刀
              damage = Math.ceil(7 * damage);
              break;
            case "gentle": // 温柔
              damage = Math.ceil(100 * heat);
            default:
              break;
          }
        }
        // 计算伤害时的怪物钩子
        monster.value.special?.forEach(
          ({ key, name, data: { multiplier, value, hpPercent } }, i) => {
            if (!monster.value) return 0;
            switch (key) {
              case "counterAtk":
                {
                  if (us.attackCount > 1) {
                    addDebuff(name);
                    damage = Math.ceil((1 - multiplier / 100) * damage);
                  }
                }
                break;
              case "repress":
                {
                  if (isCrt) {
                    addDebuff(name);
                    damage = Math.ceil((1 - multiplier / 100) * damage);
                  }
                }
                break;
              case "restore":
                {
                  if (
                    (monsterCurHp.value * 100) / monsterHp.value <
                    hpPercent
                  ) {
                    if (monster.value.hasFix) break;
                    addDebuff(name);
                    damage = 0;
                    monsterCurHp.value = Math.min(
                      monsterCurHp.value +
                        Math.ceil((monsterHp.value * value) / 100),
                      monsterHp.value
                    );
                    monster.value.hasFix = true;
                  }
                }
                break;
              case "palsy":
                {
                  if (!isCrt) {
                    addDebuff(name);
                    damage = Math.ceil((1 - multiplier / 100) * damage);
                  }
                }
                break;
              default:
                break;
            }
          }
        );
        // 影刃
        const shadowBladeValue = getSkillValueBySkill("shadowBlade");
        if (shadowBladeValue) {
          damage += Math.ceil(shadowBladeValue * 0.001 * monsterCurHp.value);
          addSkillText("shadowBlade");
        }
        //
        // 锋利
        const sharpValue = getSkillValueBySkill("sharp");
        if (sharpValue) {
          damage += Math.ceil(sharpValue * 0.2 * damage);
          addSkillText("sharp");
        }
        // 裂创
        const tearInjuryValue = getSkillValueBySkill("tearInjury");
        if (
          tearInjuryValue &&
          Math.random() * 100 < 3 + 0.5 * tearInjuryValue
        ) {
          damage += Math.ceil(
            (0.2 / (monster.value.isBoss ? 6 : 1)) * monsterCurHp.value
          );
          addSkillText("tearInjury");
        }
        return damage;
      }
      if (randomValue <= hitChance) {
        const damage = countDamage();
        hitAction(damage);
        text += `造成 <span class="hp">${damage}</span> 伤害`;
      } else if (
        randomValue > hitChance &&
        randomValue <= hitChance + dodgeChance
      ) {
        text += `<span class="hp">${monster.value.name}</span> 闪避了这次攻击`;
        //    未命中怪物钩子
        monster.value.special?.forEach(
          ({ key, name, data: { multiplier, value, hpPercent } }) => {
            if (!monster.value) return 0;
            switch (key) {
              case "vampirism":
                {
                  addDebuff(name);
                  monsterCurHp.value = Math.min(
                    monsterCurHp.value + Math.ceil(monsterHp.value * 0.01),
                    monsterHp.value
                  );
                }
                break;
              default:
                break;
            }
          }
        );
      } else {
        const damage = Math.ceil(countDamage() / 2);
        text += "砍偏了！";
        hitAction(damage);
        text += `造成 <span class="hp">${damage}</span> 伤害`;
      }
    }
    pushMessage(text);
    if (!monsterCurHp.value) {
      // 击杀怪物
      pushMessage(`击杀 <span class="hp">${monster.value.name}</span>`);

      // 击溃
      const routValue = getSkillValueBySkill("rout");
      if (routValue) {
        gameState.value.extraAtk += routValue;
        pushMessage(
          `<b>${equipAffixCollectionRogue["rout"].name}</b>，增加 <span class="grow">${routValue}</span> 点攻击力`
        );
      }

      monster.value.isBoss
        ? gameState.value.killBoss++
        : gameState.value.killMonster++;

      // 获得金币
      const gold = Math.ceil(
        (0.8 + Math.pow(Math.random(), 2)) *
          DROP_GOLD_BASIC *
          degreeConfig.value.goldXFn() *
          DROP_GOLD_LEVEL_R *
          (monster.value.isBoss ? DROP_GOLD_BOSS_R : 1)
      );
      addGold(gold);

      if (monster.value.isBoss) {
        if (isCustomMonster()) {
          us.equipRandomTimes += 2;
          startSelectEquip(userEquipCollection.value.legend, true);
          startSelectEquip(userEquipCollection.value.legend, true);
        }
        us.equipRandomTimes += 1;
        startSelectEquip(userEquipCollection.value.legend, true);
      }

      // 获得经验
      const exp = Math.ceil(
        degreeConfig.value.expXFn() *
          (monster.value.isBoss ? DROP_EXP_BOSS_R : 1)
      );
      addeExp(exp);
      clearMonster();
      writeSaved();
    }
  }
  function clearMonster() {
    us.attackCount = 0;
    monster.value = undefined;
    monsterHp.value = 0;
    monsterCurHp.value = 0;
  }
  // 挑战特殊BOSS
  function challengeSpecialBoss(x: number) {
    const tempMonster = Object.assign(
      { level: level.value, isBoss: true },
      Object.values(CUSTOM_MONSTER)[us.specialBoss]
    );
    tempMonster.hp = Math.ceil(
      Math.pow(level.value, degreeConfig.value.bossGrowX) *
        tempMonster.hp *
        x *
        hpRate.value
    );
    debutMonster(tempMonster);
    us.specialBoss++;
    gameState.value.readyBoss = undefined;
    gameState.value.readyTime = 0;
  }
  function isCustomMonster() {
    return Reflect.has(CUSTOM_MONSTER, monster.value?.name ?? "");
  }
  // Boss准备登场
  function checkBossReady(delta: number) {
    if (isCustomMonster()) return;
    if (gameState.value.gameTime >= 1000 * 60 * 30 && us.specialBoss === 3) {
      challengeSpecialBoss(1);
    } else if (
      gameState.value.gameTime >= 1000 * 60 * 25 &&
      us.specialBoss === 2
    ) {
      challengeSpecialBoss(0.8);
    } else if (
      gameState.value.gameTime >= 1000 * 60 * 20 &&
      us.specialBoss === 1
    ) {
      challengeSpecialBoss(0.5);
    } else if (
      gameState.value.gameTime >= 1000 * 60 * 15 &&
      us.specialBoss === 0
    ) {
      challengeSpecialBoss(0.2);
    } else {
      if (level.value > us.readBossLevel) {
        us.readBossLevel = level.value;
        gameState.value.readyBoss = createMonster(true);
        gameState.value.readyTime = 30 * 1000;
      } else if (gameState.value.readyTime > 0) {
        gameState.value.readyTime = Math.max(
          gameState.value.readyTime - delta,
          0
        );
      } else if (!gameState.value.readyTime && gameState.value.readyBoss) {
        gameState.value.readyBoss = undefined;
      }
    }
  }
  // 强制挑战BOSS
  function challengeBoss() {
    if (!gameState.value.readyBoss) return;
    debutMonster(gameState.value.readyBoss);
    gameState.value.readyBoss = undefined;
    gameState.value.readyTime = 0;
  }
  function recoverHp(hp: number) {
    const rt = gameState.value.remainingTime;
    const recoverdTime = Math.min(
      gameState.value.remainingTime + hp * 600,
      playerMaxRemainingTime.value
    );
    pushMessage(
      `恢复生命力 <span class="grow">${Math.ceil(
        (recoverdTime - rt) / 600
      )}</span>`
    );
    gameState.value.remainingTime = recoverdTime;
  }
  function addeExp(exp: number) {
    const extraExp = Math.ceil(
      playerEquipsFilters.value.moreExpValue * 0.1 * exp
    );
    exp += extraExp;
    pushMessage(`获得经验 <span class="info">${exp}</span>`);
    let lastLevel = playerLevel.value;
    gameState.value.exp += exp;

    if (playerLevel.value > lastLevel) {
      pushMessage(
        `等级提升至 <span class="info">${playerLevel.value}</span> 级`
      );
      // 升级
      // 回满血
      gameState.value.remainingTime = playerMaxRemainingTime.value;
      pushMessage(`生命力已回满`);
      // 升几级选几个装备
      us.equipRandomTimes += playerLevel.value - lastLevel;
      if (waitSelectEquips.value.length) return;
      startSelectEquip();
    }
  }
  function addGold(gold: number) {
    const extraGold = Math.ceil(
      playerEquipsFilters.value.moreGoldValue * 0.1 * gold
    );
    gold += extraGold;

    pushMessage(`获得金币 <span class="gold">${gold}</span>`);
    gameState.value.gold += gold;
  }
  // 推送消息
  async function pushMessage(text: string) {
    fightMessageList.value.push(text);
    if (gameState.value.isMessageScrollAuto) {
      await nextTick();
      document
        .getElementById(`rogue-message-${fightMessageList.value.length - 1}`)
        ?.scrollIntoView({ behavior: "smooth" });
      if (fightMessageList.value.length > 60) {
        fightMessageList.value = fightMessageList.value.slice(30);
      }
    }
  }
  // 获取用户解锁的装备
  async function getUserEquipCollection(dg: number) {
    if (dg < 2) {
      userEquipCollectionIds.value = Object.keys(equipCollection).map(Number);
    } else {
      const res = await socketEmitAsync<number[]>({
        type: "getUserEquipCollection",
      });
      userEquipCollectionIds.value = res;
    }
  }
  // 用可用的装备合集根据品质分类
  const userEquipCollection = computed(() => {
    const equips = userEquipCollectionIds.value.map((id) => {
      return {
        ...equipCollection[id],
        equipId: id,
        id: String(id),
        reinforcedLevel: 0,
        isLock: false,
      };
    });
    function filterEquipByQuilty(q: EquipQuality) {
      return equips
        .filter(({ quality }) => quality.includes(q))
        .map((data) => ({ ...data, quality: q }));
    }
    return {
      normal: filterEquipByQuilty("normal"),
      race: filterEquipByQuilty("race"),
      epic: filterEquipByQuilty("epic"),
      // unique: filterEquipByQuilty('unique'),
      legend: filterEquipByQuilty("legend"),
      antiquity: filterEquipByQuilty("antiquity"),
    };
  });
  // 待选择的装备
  const waitSelectEquips = ref<
    { equipList: EquipItemType[]; disabledRerandom: boolean }[]
  >([]);
  // 随机生成装备
  function createEquipWithTimes(equipList: EquipItemType[], times: number) {
    equipList = [...equipList];
    const res: EquipItemType[] = [];
    const list = Object.values(CUSTOM_EQUIPS).filter(({ id }) => {
      if (
        id === "selectiveExpansion" &&
        playerEquipsFilters.value.selectiveExpansion
      )
        return false;
      if (id === "upgradeLoot" && playerEquipsFilters.value.upgradeLoot)
        return false;
      return true;
    });
    const sRate = 0.08;
    while (times-- > 0) {
      // 概率出特殊道具
      if (Math.random() < sRate) {
        if (!list.length) break;
        const index = Math.floor(Math.random() * list.length);
        res.push(list[index] as EquipItemType);
        list.splice(index, 1);
        continue;
      }
      // 正常装备
      if (equipList.length) {
        const index = Math.floor(Math.random() * equipList.length);
        res.push(equipList[index]);
        equipList.splice(index, 1);
      } else {
        if (!list.length) break;
        const index = Math.floor(Math.random() * list.length);
        res.push(list[index] as EquipItemType);
        list.splice(index, 1);
      }
    }
    return res;
  }
  // 开启装备选择面板
  function startSelectEquip(
    list?: (typeof userEquipCollection.value)["normal"],
    disabledRerandom = false
  ) {
    stopGame();
    const lucky7Value = getSkillValueBySkill("lucky7");
    let equips = userEquipCollection.value.race;
    const isLegend = lucky7Value && Math.random() < 0.07 * lucky7Value;
    if (playerEquipsFilters.value.upgradeLoot) {
      equips = userEquipCollection.value.epic;
    }
    if (isLegend) {
      equips = userEquipCollection.value.legend;
    }
    const equipList: EquipItemType[] = createEquipWithTimes(
      list || equips,
      3 + playerEquipsFilters.value.selectiveExpansion
    );
    waitSelectEquips.value.push({ equipList, disabledRerandom });
  }
  // 装备选择
  function selectEquip(item: EquipItemType) {
    const sellPrice = EquipPriceEnum[item.quality];
    if (gameState.value.gold < sellPrice) {
      ElMessage.warning("金币不足！");
      return;
    }
    gameState.value.gold -= sellPrice;
    waitSelectEquips.value.shift();
    playerEquips.value.push(item);
    pushMessage(
      `花费 <span class="gold">${sellPrice}</span> 金币，获得 <span class="${item.quality}">${item.name}</span> 一件`
    );
    if (--us.equipRandomTimes > 0) {
      startSelectEquip();
    } else if (!waitSelectEquips.value.length) {
      resumeGame();
    }
  }
  function rerandomRequip() {
    const cost = 100;
    if (gameState.value.gold < cost) {
      ElMessage.warning("金币不足！");
      return;
    }
    gameState.value.gold -= cost;
    startSelectEquip();
    pushMessage(`花费 <span class="gold">${cost}</span> 金币，重随一次`);
    waitSelectEquips.value.shift();
  }
  function cancelSelectEquip() {
    waitSelectEquips.value.shift();
    resumeGame();
  }
  // 计算分数
  const countScoreFn = {
    time() {
      return Math.floor(gameState.value.gameTime / 1000);
    },
    boss() {
      return gameState.value.killBoss * 600;
    },
    monster() {
      return gameState.value.killMonster * 5;
    },
    equip() {
      return playerEquips.value.length * 10;
    },
    gold() {
      return Math.ceil(
        Math.sqrt(Math.max(0, gameState.value.gold - 1000)) / 10
      );
    },
    total() {
      return Math.ceil(
        degreeConfig.value.scoreX *
          (this.time() +
            this.boss() +
            this.monster() +
            this.equip() +
            this.gold())
      );
    },
  };
  // 技能最大cd
  function countSkillCD(type: string, value: number) {
    return CD_TIME_TABLE[type] * Math.pow(0.8, value - 1);
  }
  // 计算技能cd百分比
  function countSkillCDPercent(type: string) {
    const item = skillCoutDownMap.value.get(type);
    if (!item) return 0;
    return parseFloat(((item.cur / item.max) * 100).toFixed(2));
  }
  // 使用技能
  function useSkill(item: SkillType) {
    if (skillCoutDownMap.value.get(item.type)?.cur) {
      pushMessage("技能还未准备就绪");
      return;
    }
    const maxCD = countSkillCD(item.type, item.value);
    skillCoutDownMap.value.set(item.type, { max: maxCD, cur: maxCD });
    attackAction(item);
  }
  // 移除装备
  function removeEquipByType(type: string) {
    const index = playerEquips.value.findIndex((item) => {
      return item.attrs.special?.some((item) => item.key === type);
    });
    if (index > -1) {
      playerEquips.value.splice(index, 1);
    }
  }
  function writeSaved() {
    const data = {
      VERSION,
      gameState: gameState.value,
      degree: degree.value,
      us,
      monster: monster.value,
      monsterHp: monsterHp.value,
      monsterCurHp: monsterCurHp.value,
      playerEquips: playerEquips.value,
      skillCoutDownMap: Array.from(skillCoutDownMap.value),
      isStop: isStop.value,
      waitSelectEquips: waitSelectEquips.value,
      userEquipCollectionIds: userEquipCollectionIds.value,
    };

    localStorage.setItem(
      STORAGE_KEY,
      encryptXor(
        encodeURIComponent(JSON.stringify(data)),
        import.meta.env.VITE_TOKEN_NAME
      )
    );
  }

  // 读取存档
  async function readSaved() {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const data = decryptXor(saved, import.meta.env.VITE_TOKEN_NAME);
        const [oMainV, oFeatV, oFixV] = data.VERSION.split(".");
        const [nMainV, nFeatV, nFixV] = data.VERSION.split(".");
        if (!(oMainV === nMainV && oFeatV === nFeatV)) {
          console.warn("存档版本不同，直接废弃");
          return;
        }
        gameState.value = data.gameState;
        degree.value = data.degree;
        us = data.us;
        monster.value = data.monster;
        monsterHp.value = data.monsterHp;
        monsterCurHp.value = data.monsterCurHp;
        playerEquips.value = data.playerEquips;
        skillCoutDownMap.value = new Map(data.skillCoutDownMap);
        isStop.value = data.isStop;
        waitSelectEquips.value = data.waitSelectEquips;
        userEquipCollectionIds.value = data.userEquipCollectionIds;
      } catch (error) {
        console.warn("[error]", "肉鸽存档解析失败");
        console.error(error);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }
  readSaved();
  return {
    gameState,
    startGame,
    stopGame,
    resumeGame,
    monster,
    monsterHp,
    monsterCurHp,
    fightMessageList,
    cancelGame,
    continueGame,
    closeGame,
    waitSelectEquips,
    selectEquip,
    playerComputedAttr,
    playerEquips,
    equipsMountLevel,
    level,
    countScoreFn,
    playerLevel,
    rerandomRequip,
    cancelSelectEquip,
    challengeBoss,
    degree,
    degreeConfig,
    meetGameNeeds,
    DEGREE_CONFIG,
    playerMaxRemainingTime,
    isStop,
    handleStopGame,
    handleResumeGame,
    playerEquipsFilters,
    skillBtns,
    useSkill,
    countSkillCDPercent,
    battleSpeed,
  };
});

/****************** 命中算法 **************************/
const BASE_HIT_CHANCE = 60; // 基础命中率百分比
const BASE_DODGE_CHANCE = 3; // 基础闪避率百分比

// Sigmoid函数
function sigmoid(x: number, k: number, x0: number) {
  return 1 / (1 + Math.exp(-k * (x - x0)));
}

const k_H = 0.1; // 命中修正函数的斜率
const x0_H = 50; // 命中修正函数的中心位置
const k_D = 0.1; // 闪避修正函数的斜率
const x0_D = 50; // 闪避修正函数的中心位置
// 计算修正后的命中和闪避率
function calculateHitAndDodgeChances(
  hitModifier: number,
  dodgeModifier: number
) {
  // 计算修正后的命中率
  const hitModifierChance = sigmoid(hitModifier, k_H, x0_H);
  const actualHitChance =
    BASE_HIT_CHANCE + hitModifierChance * (100 - BASE_HIT_CHANCE);

  // 计算修正后的闪避率
  const dodgeModifierChance = sigmoid(dodgeModifier, k_D, x0_D);
  const actualDodgeChance =
    BASE_DODGE_CHANCE + dodgeModifierChance * (100 - BASE_DODGE_CHANCE);

  return {
    hitChance: actualHitChance,
    dodgeChance: actualDodgeChance,
  };
}

function getBasicAttrValue(basicVal: number, quality: string) {
  const mag = quality === "race" ? 1.1 : quality === "epic" ? 1.3 : 1;

  return Math.ceil(basicVal * mag);
}

/** 自定义装备 */

export const CUSTOM_EQUIPS = {
  战利品扩容: {
    quality: "legend",
    equipId: -970001,
    id: "selectiveExpansion",
    reinforcedLevel: 0,
    isLock: false,
    name: "战利品扩容",
    type: "battery",
    attrs: {
      basic: {},
      special: [
        {
          key: "selectiveExpansion",
          data: { value: 1 },
        },
      ],
    },
  },
  战利品升级: {
    quality: "legend",
    equipId: -970002,
    id: "upgradeLoot",
    reinforcedLevel: 0,
    isLock: false,
    name: "战利品升级",
    type: "battery",
    attrs: {
      basic: {},
      special: [
        {
          key: "upgradeLoot",
          data: { value: 1 },
        },
      ],
    },
  },
  财富宝石: {
    quality: "legend",
    equipId: -970003,
    id: "moreGold",
    reinforcedLevel: 0,
    isLock: false,
    name: "财富宝石",
    type: "battery",
    attrs: {
      basic: {},
      special: [
        {
          key: "moreGold",
          data: { value: 1 },
        },
      ],
    },
  },
  智慧宝石: {
    quality: "legend",
    equipId: -970004,
    id: "moreExp",
    reinforcedLevel: 0,
    isLock: false,
    name: "智慧宝石",
    type: "battery",
    attrs: {
      basic: {},
      special: [
        {
          key: "moreExp",
          data: { value: 1 },
        },
      ],
    },
  },
  西瓜: {
    quality: "legend",
    equipId: -970005,
    id: "recoverHp",
    reinforcedLevel: 0,
    isLock: false,
    name: "西瓜",
    type: "watermelon",
    attrs: {
      basic: {},
      special: [
        {
          key: "recoverHp",
          data: {
            value: WM_R_HP, // 回复
            value2: WM_HP,
          },
        },
      ],
    },
  },
};

/** 自定义怪物 */
export const CUSTOM_MONSTER = {
  甘: {
    name: "甘",
    hp: 40000,
    def: 200,
    evs: 10,
    crtdef: 10,
    special: [
      {
        name: "恐吓",
        key: "intimidate",
        data: {
          multiplier: 10,
        },
      },
    ],
  },
  文: {
    name: "文",
    hp: 50000,
    def: 350,
    evs: 40,
    crtdef: 20,
    special: [
      {
        name: "恐吓",
        key: "intimidate",
        data: {
          multiplier: 10,
        },
      },
      {
        name: "无畏",
        key: "fearless",
        data: {
          hpPercent: 30,
          value: 100,
        },
      },
    ],
  },
  崔: {
    name: "崔",
    hp: 60000,
    def: 500,
    evs: 50,
    crtdef: 30,
    special: [
      {
        name: "反击",
        key: "counterAtk",
        data: {
          multiplier: 10,
        },
      },
      {
        name: "恐吓",
        key: "intimidate",
        data: {
          multiplier: 20,
        },
      },
      {
        name: "无畏",
        key: "fearless",
        data: {
          hpPercent: 30,
          value: 200,
        },
      },
      {
        name: "镇压",
        key: "repress",
        data: {
          multiplier: 10,
        },
      },
    ],
  },
  aring: {
    name: "aring",
    hp: 999999,
    def: 9999,
    evs: 99,
    crtdef: 90,
    special: [
      {
        name: "镇压",
        key: "repress",
        data: {
          multiplier: 50,
        },
      },
      {
        name: "修复",
        key: "restore",
        data: {
          hpPercent: 50,
          value: 50,
        },
      },
      {
        name: "恐吓",
        key: "intimidate",
        data: {
          multiplier: 50,
        },
      },
      {
        name: "反击",
        key: "counterAtk",
        data: {
          multiplier: 50,
        },
      },
      {
        name: "麻痹",
        key: "palsy",
        data: {
          multiplier: 50,
        },
      },
    ],
  },
};

/** 字符串加解密 */
function encryptXor(data: any, key: string) {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const keyBuffer = encoder.encode(key);
  let encrypted = "";

  for (let i = 0; i < dataBuffer.length; i++) {
    encrypted += String.fromCharCode(
      dataBuffer[i] ^ keyBuffer[i % keyBuffer.length]
    );
  }

  // 将加密后的字符串转换为Uint8Array，再转换为Base64字符串
  const encryptedBuffer = encoder.encode(encrypted);
  const base64Encoded = btoa(
    String.fromCharCode.apply(null, new Uint8Array(encryptedBuffer) as never)
  );

  return base64Encoded;
}

function decryptXor(encryptedData: string, key: string) {
  const encoder = new TextEncoder();
  const decoder = new TextDecoder("utf-8");

  // 先将Base64字符串转换为Uint8Array
  const base64Decoded = atob(encryptedData);
  const encryptedBuffer = new Uint8Array(base64Decoded.length);
  for (let i = 0; i < base64Decoded.length; i++) {
    encryptedBuffer[i] = base64Decoded.charCodeAt(i);
  }

  const encryptedString = decoder.decode(encryptedBuffer);
  const keyBuffer = encoder.encode(key);
  let decryptedString = "";

  for (let i = 0; i < encryptedString.length; i++) {
    decryptedString += String.fromCharCode(
      encryptedString.charCodeAt(i) ^ keyBuffer[i % keyBuffer.length]
    );
  }

  return JSON.parse(decodeURIComponent(decryptedString));
}
